import { useLoaderData, useRevalidator } from 'react-router';
import { useEffect } from 'react';

import TeamDashboardProjectCard from './components/TeamDashboardProjectCard';
import supabase from '@/utils/supabase';
import type { ProjectRole } from '@/utils/status';
import { Separator } from '@/components/ui/separator';

export type Project = {
  id: string;
  project_name: string;
  status: string;
  due_date: string | null;
  clients: { company_name: string } | null;
  deliverables: {
    id: string;
    deliverables_team: { team_status: string } | null;
    is_ingesting: boolean;
  }[];
};

/**
 * Fetches the authenticated user's projects and their role in each project.
 *
 * @returns An object with `openProjects` (active projects), `closedProjects` (archived projects), and `membershipMap` (project ID to role mapping).
 *
 * @throws Response with status 401 if the user is not authenticated.
 * @throws Response with status 500 if a database query fails.
 */
export async function loader() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const userId = session?.user?.id;

  if (!userId) {
    throw new Response('Unauthorised', { status: 401 });
  }

  const [
    { data: openProjects, error: openProjectsError },
    { data: closedProjects, error: closedProjectsError },
    { data: memberships, error: membershipsError },
  ] = await Promise.all([
    supabase
      .from('projects')
      .select(
        'id, project_name, status, due_date, clients(company_name), deliverables(id, deliverables_team(team_status), is_ingesting)'
      )
      .neq('status', 'cancelled')
      .neq('status', 'approved')
      .order('created_at', { ascending: false }),
    supabase
      .from('projects')
      .select(
        'id, project_name, status, due_date, clients(company_name), deliverables(id, deliverables_team(team_status), is_ingesting)'
      )
      .in('status', ['approved', 'cancelled'])
      .order('created_at', { ascending: false }),
    supabase
      .from('project_members')
      .select('project_id, project_role')
      .eq('user_id', userId),
  ]);

  if (openProjectsError || !openProjects) {
    throw new Response('Failed to load open projects', { status: 500 });
  }

  if (closedProjectsError || !closedProjects) {
    throw new Response('Failed to load closed projects', { status: 500 });
  }

  if (membershipsError) {
    throw new Response('Failed to load project memberships', { status: 500 });
  }

  const membershipMap: Record<string, ProjectRole> = Object.fromEntries(
    (memberships ?? []).map((m) => [
      m.project_id,
      m.project_role as ProjectRole,
    ])
  );

  return { openProjects, closedProjects, membershipMap };
}

/**
 * Renders separated sections of active and archived projects.
 *
 * Automatically revalidates project data every 10 seconds to keep information current.
 */
export default function TeamIndex() {
  const { revalidate } = useRevalidator();

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') revalidate();
    }, 10_000);
    return () => clearInterval(interval);
  }, [revalidate]);

  const { openProjects, closedProjects, membershipMap } = useLoaderData() as {
    openProjects: Project[];
    closedProjects: Project[];
    membershipMap: Record<string, ProjectRole>;
  };

  const renderProjectCards = (projects: Project[]) =>
    projects.map((project) => (
      <TeamDashboardProjectCard
        key={project.id}
        project={project}
        projectRole={membershipMap[project.id] ?? 'reviewer'}
      />
    ));

  return (
    <div className="px-4">
      <div className="flex w-full flex-wrap content-start items-start gap-4">
        <h1 className="w-full basis-full text-2xl font-bold">Active</h1>
        {renderProjectCards(openProjects)}
        <Separator className="mt-4 w-full!" />
        <h1 className="w-full basis-full text-2xl font-bold">Archive</h1>
        {renderProjectCards(closedProjects)}
      </div>
    </div>
  );
}
